/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import { networkBaseQueryV2 } from './queries';
import removeEmptyProps from '../helpers/removeEmptyProps';
import deserialize from '../helpers/getroPropertyMapper';

export const movementsApi = createApi({
  reducerPath: 'movementsApi',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['movements'],
  endpoints: (builder) => ({
    getMovements: builder.query({
      query: ({
        sortBy,
        sortDirection,
        page = 1,
        perPage = 20,
        dateFrom,
        dateTo,
        movementType,
        companyIds,
        sharedListIds,
        contactScope,
      }) => ({
        url: 'movements',
        params: removeEmptyProps({
          sort_by: sortBy,
          sort_direction: sortDirection,
          page,
          per_page: perPage,
          date_from: dateFrom,
          date_to: dateTo,
          movement_type: movementType,
          company_ids: companyIds,
          shared_list_ids: sharedListIds,
          contact_scope: contactScope,
        }),
      }),
      transformResponse: (response) => {
        const deserialized = deserialize(response);

        return {
          items: deserialized.items || deserialized || [],
          meta: response.meta,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, perPage, ...rest } = queryArgs || {};
        const sortedRest = Object.keys(rest)
          .sort()
          .reduce((acc, key) => {
            acc[key] = rest[key];
            return acc;
          }, {});
        return `${endpointName}(${JSON.stringify(sortedRest)})`;
      },
      merge: (currentCache, incomingState, { arg }) => {
        if (incomingState && incomingState.items && Array.isArray(incomingState.items)) {
          if (arg?.page === 1) {
            currentCache.items = incomingState.items;
          } else {
            if (!currentCache.items) {
              currentCache.items = [];
            }
            const existingIds = new Set(currentCache.items.map((item) => item.id));
            const newItems = incomingState.items.filter((item) => !existingIds.has(item.id));
            currentCache.items.push(...newItems);
          }
          if (incomingState.meta) {
            currentCache.meta = incomingState.meta;
          }
        }
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        const { page: currentPage, ...currentRest } = currentArg || {};
        const { page: previousPage, ...previousRest } = previousArg || {};

        if (!isEqual(currentRest, previousRest)) return true;

        return currentPage !== previousPage;
      },
      providesTags: ['movements'],
    }),
    getMovementsCompanies: builder.query({
      query: ({ query }) => ({
        url: 'movements/companies',
        params: removeEmptyProps({
          q: query,
          per_page: 50,
        }),
      }),
      transformResponse: (response) => {
        const organizations = deserialize(response);
        const orgOptions = (Array.isArray(organizations) ? organizations : []).map((org) => ({
          value: org.id,
          label: org.name || org.attributes?.name || '',
        }));

        const unlinkedNames = response?.meta?.unlinkedCompanyNames || [];
        const nameOptions = unlinkedNames.map((name) => ({
          value: `name:${name}`,
          label: name,
        }));

        return [...orgOptions, ...nameOptions];
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs?.query || ''})`,
    }),
  }),
});

export const { useGetMovementsQuery, useLazyGetMovementsQuery, useLazyGetMovementsCompaniesQuery } = movementsApi;
